// Heads-up page (THT-729) — every user-facing string, route and tuning knob lives here so the
// copy swaps Brittany/John may ask for after mockup sign-off are one-line edits, and so the
// component never carries a string literal. Copy is verbatim from mockups v1.

/**
 * Lookup-only carrier-name endpoint owned by THT-728 (`services/highway`, route
 * `GET /carrier-name/{mc_or_dot_number}`). Single integration point: change this one
 * constant if the backend ever moves it.
 *
 * Contract (`services/highway/src/apiTypes.ts` → `CarrierNameLookupResponse`):
 *   `{ found: boolean, legal_name?: string, identifier_type?: 'MC' | 'DOT',
 *      ambiguous?: boolean, alternate?: { identifier_type, legal_name } }`
 * An unknown number is an expected 200 `{ found: false }`; Highway being down is a 503.
 */
export const CARRIER_NAME_LOOKUP_ROUTE = '/highway/carrier-name';

/**
 * Prefill endpoint owned by THT-711 workstream (a) (`services/highway`, route
 * `GET /highway/carrier-prefill/{mc_or_dot_number}?identifier_type=MC|DOT`). Called once, on
 * the confirm screen, with the identifier the customer has just CONFIRMED — hence the explicit
 * type rather than the try-both guessing the name lookup does.
 *
 * Contract: `{ found: boolean, prefill?: { legal_name, dba_name?, address?: { street_number,
 * street_name, unit?, city, state, zip }, phone?, email?, years_in_business?, owned_tractors?,
 * owned_trailers?, contact_first_name?, contact_last_name? } }`. There are deliberately NO tax
 * identifiers in it — EIN prefill is deferred pending John's masked-confirm call.
 */
export const CARRIER_PREFILL_ROUTE = '/highway/carrier-prefill';

/** Query parameter naming which identifier the confirmed number is. */
export const CARRIER_PREFILL_IDENTIFIER_TYPE_PARAM = 'identifier_type';

/**
 * Client-side ceiling on the prefill call, same pattern (and same reasoning) as
 * CARRIER_LOOKUP_TIMEOUT_MS: the server fails fast first and this only catches a hung
 * connection. Prefill is a convenience — it races the customer patch and whichever way it
 * goes the flow continues, so this timeout can only ever cost the customer empty fields.
 */
export const CARRIER_PREFILL_TIMEOUT_MS = 12000;

/**
 * How long the handoff to Business Info will wait on the prefill AFTER the customer patch has
 * already landed. The two requests go out together, so by this point the prefill has normally
 * had its whole life to answer and this costs nothing — it exists for the case where it has
 * not, and it is short because the customer has finished everything this page asked of them
 * and is watching a spinner. A convenience feature does not get to hold that spinner for the
 * full CARRIER_PREFILL_TIMEOUT_MS.
 *
 * A prefill that lands after the grace still writes its session data; Business Info will have
 * read already, but Personal Info and Assets mount later and pick it up.
 */
export const CARRIER_PREFILL_HANDOFF_GRACE_MS = 800;

/**
 * Slack-only "this trucker is stuck" endpoint owned by THT-738 (`services/highway`, route
 * `POST /highway/verification-stuck`). The single point of coupling between this workstream
 * and the backend one — change this constant if the route ever moves.
 *
 * Body: `{ attempted_numbers: string[] }`. It resolves the customer from the authorizer's
 * `subId` itself, so nothing identifying is sent from here. There is no response worth
 * reading: the call is fire-and-forget and a failure is swallowed.
 */
export const VERIFICATION_STUCK_ROUTE = '/highway/verification-stuck';

/**
 * How many consecutive failed verification attempts before customer service is pinged.
 * Three is John's number (2026-08-06): enough that a single typo does not page anyone,
 * few enough that a trucker who is genuinely stuck gets a human before they give up.
 */
export const VERIFICATION_STUCK_THRESHOLD = 3;

/**
 * Cap on the numbers carried in the stuck alert. The backend validates the same ceiling, and
 * the alert is a starting point for a human conversation — every number a customer has ever
 * typed is noise, the last handful is context.
 */
export const VERIFICATION_STUCK_MAX_NUMBERS = 5;

/**
 * Namespace every sessionStorage key this page writes shares. The customer id is baked into
 * each key, so the keys cannot be listed ahead of time — `UserContext`'s logout purge sweeps
 * this PREFIX instead. That matters: the failure state holds MC/DOT numbers the customer
 * typed, and a stale "we already told the team" flag would have the page promise a human to
 * whoever logs in next in the same tab (auth0Logout is a same-tab redirect, so sessionStorage
 * survives it).
 */
export const HEADS_UP_STORAGE_PREFIX = 'tach.headsUp.';

/**
 * sessionStorage keys for the consecutive-failure run, keyed per customer so a shared browser
 * never inherits someone else's count. Session-scoped on purpose: the counter is about one
 * sitting of somebody getting stuck, not a lifetime tally.
 */
export const verificationFailureStateKey = (customerId: string) =>
  `${HEADS_UP_STORAGE_PREFIX}verificationFailures.${customerId}`;
export const verificationStuckNotifiedKey = (customerId: string) =>
  `${HEADS_UP_STORAGE_PREFIX}verificationStuckNotified.${customerId}`;

/**
 * sessionStorage keys the prefill writes and the signup steps read back.
 *
 * `businessInfo` and `personalInfo` are the EXISTING shapes `populateApplicationData` writes and
 * BusinessInfo / AccountInfo hydrate from — the prefill merges into them rather than inventing a
 * parallel channel, so returning-applicant hydration is untouched. They are named here rather
 * than repeated as literals, and they are already on `UserContext`'s logout purge list.
 *
 * The two NEW keys carry HEADS_UP_STORAGE_PREFIX so that purge's namespace sweep covers them
 * for free. That is not cosmetic: they hold a carrier's truck counts and the record of which
 * values came from that carrier, and auth0Logout is a same-tab redirect, so an un-swept key
 * would greet whoever logs in next on a shared cab or kiosk browser with someone else's fleet.
 */
export const BUSINESS_INFO_STORAGE_KEY = 'businessInfo';
export const PERSONAL_INFO_STORAGE_KEY = 'personalInfo';
export const ASSET_PREFILL_STORAGE_KEY = `${HEADS_UP_STORAGE_PREFIX}assetPrefill`;
export const PREFILL_SUGGESTIONS_STORAGE_KEY = `${HEADS_UP_STORAGE_PREFIX}prefillSuggestions`;

/**
 * Which carrier the prefill has already been looked up for. Business Info re-fetches when this
 * does not match the carrier on the record, which is what makes the prefill survive a new tab or
 * a restarted browser — sessionStorage does not, and the heads-up page that fetched it in the
 * first place never renders again once the MC/DOT is saved.
 */
export const PREFILL_LOOKUP_MARKER_KEY = `${HEADS_UP_STORAGE_PREFIX}prefillLookedUp`;

/**
 * What Business Info says while that re-fetch is in flight.
 *
 * Without it the customer met an empty form that silently rewrote itself a second or two later —
 * worse than a wait, because a form that fills itself in under your hands reads as a glitch, and
 * anyone who started typing watched the layout shift around them. Naming the MC/DOT is the point:
 * it explains where the details are about to come from, so the values that land are expected
 * rather than mysterious.
 */
export const PREFILL_LOOKUP_LOADING_TEXT =
  'Checking your MC/DOT for your business details...';

/**
 * How long that loading state may hold the form before it gives up and shows the fields anyway.
 *
 * Deliberately far shorter than CARRIER_PREFILL_TIMEOUT_MS: the lookup itself is allowed twelve
 * seconds because a late prefill is still worth having, but blocking a signup form for twelve
 * seconds is not — a customer who cannot start typing is a customer who leaves. Past this point
 * the fetch carries on in the background and still fills whatever the customer has not answered.
 */
export const PREFILL_LOOKUP_LOADING_MAX_MS = 4000;

/**
 * Client-side ceiling on the lookup. Deliberately longer than the backend's own
 * `HIGHWAY_CARRIER_LOOKUP_TIMEOUT_MS` (8s) so the server's fail-fast wins first and this
 * only catches a hung connection. Either way a slow Highway never blocks the signup flow.
 */
export const CARRIER_LOOKUP_TIMEOUT_MS = 12000;

/** How long each staged loading message stays on screen before the next one takes over. */
export const LOADING_STAGE_INTERVAL_MS = 2200;

/**
 * Where the progress fill opens. A bar that starts at a true zero reads as broken for its
 * first frames, so it comes up with a sliver already showing; the remaining travel is scaled
 * into what is left.
 */
export const LOADING_PROGRESS_HEAD_START = 0.04;

/**
 * How long the bar takes to run from wherever it had crawled to to full once the lookup
 * actually settles. The crawl is only an estimate — this is the part that is true — so the
 * screen waits for it: a bar that vanishes mid-travel reads as an interruption, and most
 * lookups return long before the crawl is anywhere near the end.
 */
export const LOADING_COMPLETION_MS = 280;

/**
 * How long the flow will wait on that completion before going on without it. Animation frames
 * stop in a backgrounded tab, and a customer who tabs away mid-lookup — on a page that asks
 * them to wait — must not come back to a signup parked on the loading screen. Timers keep
 * running where frames do not, so this is the backstop that keeps a decorative animation from
 * ever becoming one more way to block the flow.
 */
export const LOADING_COMPLETION_GIVE_UP_MS = 1200;

/**
 * Digits only, up to 10 — mirrors `CARRIER_IDENTIFIER_PATTERN` in
 * `common/types-and-constants/src/constants/carrierIdentity.ts` (which the lookup endpoint
 * enforces with a 400) and matches what BusinessInfo accepts today.
 *
 * BusinessInfo reference (`src/components/signup/customers/BusinessInfo.tsx`, the MC/DOT input):
 * it asks for one identifier at a time and caps it per type — `maxLength={carrierType === 'MC'
 * ? 8 : 10}`, with the same cap re-applied in its onChange. This page asks a SINGLE "MC or DOT"
 * question, so its cap has to be the UNION of the two: max(8, 10) = 10. Anything stricter here
 * would reject DOT numbers the rest of the flow admits.
 */
export const MC_DOT_PATTERN = /^[0-9]{1,10}$/;
export const MC_DOT_MAX_LENGTH = 10;

/**
 * The MC half of that union. The leased-on path (THT-752) lets the customer name which identifier
 * they are entering, and the field's cap follows that answer the way BusinessInfo's does
 * (`maxLength={carrierType === 'MC' ? 8 : 10}`) — so an MC can never be typed over-length, and a
 * DOT already typed is trimmed if the customer switches to MC.
 */
export const MC_NUMBER_MAX_LENGTH = 8;

/**
 * The customer patch is the only write on this page — unlike the lookup, a failure here
 * loses the answer, so it gets retries before the customer is told about it.
 */
export const CARRIER_PATCH_RETRIES = 2;

/**
 * Which of the two customer columns an entered identifier is written to. Values mirror
 * `HighwayIdentifierType` in `common/types-and-constants`, which is what the lookup
 * returns as `identifier_type`.
 */
export const CARRIER_TYPE_MC = 'MC';
export const CARRIER_TYPE_DOT = 'DOT';

/** Answers to the "own authority?" question. */
export const AUTHORITY_YES = 'yes';
export const AUTHORITY_NO = 'no';

/**
 * Step key handed to SignupBreadcrumbs while the heads-up page is showing. It is deliberately
 * NOT an ApplicationStep: any key outside the breadcrumb step map makes that component fall
 * through to its logo + Log Out bar, which is exactly the chrome this page wants — same top
 * bar as the later steps, minus the breadcrumbs.
 */
export const HEADS_UP_TOPBAR_STEP = 'heads_up';

/**
 * Master switch for the whole feature — the heads-up page, the Business Info slim-down that
 * depends on it, and the Highway prefill the page fires. ON as of THT-711, the last ticket of
 * the set: the three are halves of one change and have to move together, since the slim-down
 * alone would take the MC/DOT field away with nothing having asked for it.
 *
 * Flipping it back to `false` is the entire rollback, and stays under test in both positions
 * (`*.headsUpDisabled.test.tsx` for off, `signup.headsUpGate` / `BusinessInfo.test` for on).
 * The gate conditions behind it — `customerIsMissingCarrierIdentity` and the resume-at-saved-step
 * rule in `src/pages/signup.tsx` — are unchanged either way.
 */
export const HEADS_UP_ENABLED = true;

/**
 * Which screen the heads-up page is showing.
 *
 * INTRO and FORM are the two halves of what used to be one screen: the welcome, "What happens
 * next" and the mobile-app mention land first with nothing to fill in, and Continue reveals the
 * authority question and its input. Neither half calls the backend — the split is purely so each
 * one is a smaller thing to take in — and the lookup still runs off the FORM half's Continue.
 *
 * Every path back from a later screen ("That's not my business", "Neither") returns to FORM, not
 * INTRO: the customer has already read the intro, and what they came back to fix is the number.
 * For the same reason a correction arriving from Business Info's "Not right?" link OPENS on FORM
 * — see `correctingSavedIdentity` on the page.
 */
export const HEADS_UP_PHASE_INTRO = 'intro';
export const HEADS_UP_PHASE_FORM = 'form';
export const HEADS_UP_PHASE_LOOKING_UP = 'looking_up';
export const HEADS_UP_PHASE_CONFIRM = 'confirm';
export const HEADS_UP_PHASE_LOOKUP_FAILED = 'lookup_failed';

/**
 * The muted text colour of the whole meta row under "What happens next" — the reassurances, the
 * time estimate, and the "Why does TACH need this info?" trigger sitting beside them all read
 * their colour from here. John asked for the trigger and the estimate to match, and two hardcoded
 * copies of the same colour are exactly how they stop matching the next time either is touched.
 */
export const HEADS_UP_META_TEXT_CLASS = 'text-gray-400';

export const HEADS_UP_COPY = {
  title: 'Welcome to TACH!',
  subtitle:
    "Let's take your first step towards earning more revenue by operating like the big fleets.",

  whatHappensNextTitle: 'What happens next',
  whatHappensNextSteps: [
    // Details name what each step actually asks for, so nothing on the later steps is a surprise.
    {
      title: 'Confirm your business',
      detail: 'Name, EIN, address, beneficial ownership, revenue',
    },
    {
      title: 'Verify your identity',
      detail: "Contact info, SSN, driver's license number",
    },
    { title: 'Add your trucks', detail: 'Year, make, model, mileage' },
  ],
  /**
   * The meta row under "What happens next", rendered left to right and dot-separated with the
   * time estimate. The reassurances lead: what signing up costs a carrier personally is the
   * question they hold back on, and it outranks how long the form takes.
   */
  reassurances: ['No personal credit check', 'No personal guarantee'],
  timeEstimate: 'Takes 5–10 minutes',
  /**
   * Collapsed by default, opened from the control beside the time estimate.
   *
   * Body is the landing page's "Why does TACH need this information?" expandable VERBATIM
   * (`tach-landing-page/src/app/select-role/page.tsx`, the `why_does_tach_need_this_info`
   * section): intro paragraph plus the full bullet list. It is a compliance explanation that
   * legal has already signed off on in that wording — reword it here and the two pages start
   * answering the same question differently.
   */
  whyTitle: 'Why does TACH need this info?',
  whyBody:
    "As a financial technology company, TACH partners with licensed financial institutions to provide business banking and financial services. To comply with U.S. banking regulations, we are required to collect certain information about your business and its owners before your account can be opened. This helps verify your identity, prevent financial crime, and ensure we're providing services responsibly and securely. We collect this information to:",
  whyBullets: [
    'Verify your business and ownership structure under federal banking and anti-money laundering laws',
    'Identify the individuals who own or control your business, as required by the Beneficial Ownership Rule',
    'Meet Know Your Customer (KYC) and Customer Identification Program (CIP) requirements, including checking your SSN and ID',
    "Ensure we're offering the right financial services for your business based on its size, industry, and operations",
    'Protect you and TACH from fraud, identity theft, and illegal activity',
  ],

  /**
   * Heading for the second half of the form, where the welcome heading no longer applies — the
   * customer has already been welcomed on the screen before this one. The subtitle deliberately
   * says nothing about the carrier registry: this screen is also the leased-on driver's screen,
   * and they never get a lookup.
   */
  authorityStepTitle: 'Tell us about your authority',
  authorityStepSubtitle:
    'This is the only thing we need from you on this screen.',
  authorityQuestion: 'Do you run under your own authority?',
  authorityYesLabel: 'Yes',
  authorityNoLabel: 'No',
  /** Back to the intro — the only place "Why does TACH need this info?" is answered. */
  backLabel: 'Back',

  mcDotQuestion: "What's your MC or DOT number?",
  /**
   * The same field, asked of a leased-on driver (THT-752). They have no authority of their own,
   * so the question cannot say "your" — but they still roll under somebody's MC/DOT, and that
   * number is what the carrier-details workflow checks. Signup used to let this path through with
   * both columns null, which dead-ended the customer at Review with nothing on screen to fix.
   */
  mcDotUnderAuthorityQuestion: 'What MC or DOT number do you run under?',
  mcDotPlaceholder: 'e.g. 149315',
  mcDotInvalid: 'Enter digits only — up to 10.',
  /**
   * The No path has no lookup to report which identifier the number is, and the digits cannot
   * say: MC and DOT ranges overlap. So it asks — one tap, and the number lands in the column the
   * customer named rather than one guessed for them.
   */
  carrierTypeQuestion: 'Is that an MC or a DOT number?',
  /** Says why we are asking a driver who just told us the authority is not theirs. */
  mcDotUnderAuthorityHint:
    "It's on the side of the truck you drive, or on your lease agreement.",

  ownerQuestion: 'Whose authority do you run under?',
  ownerPlaceholder: 'Company name',

  // The mobile-app mention on the default state. Split in three so the product name can carry
  // an underline in place without the component holding a string literal.
  mobileAppPrefix: 'The ',
  mobileAppName: 'TACH: For Truckers',
  mobileAppSuffix:
    ' mobile app is available for download on both the App Store and Google Play',

  continueLabel: 'Continue',
  /** Shown on the primary button while the customer patch is in flight. */
  processingLabel: 'Processing…',

  // Staged loading messages (criterion 4 — at least two distinct messages, never a bare spinner).
  loadingStages: [
    'Looking up your authority…',
    'Pulling your business details…',
    'Almost there — preparing your application…',
  ],
  loadingSubtitle: "This usually takes a few seconds. Don't close this page.",
  loadingStepLabels: ['Authority', 'Business details', 'Your application'],
  // The bar is deliberately indeterminate to assistive tech (no aria-valuenow): its position
  // is a time-based estimate, not a measured fraction of the lookup.
  loadingProgressLabel: 'Carrier lookup progress',

  // Legal-name confirm — single match.
  confirmTitle: 'Is this your business?',
  confirmYesLabel: "Yes, that's my business",
  confirmNoLabel: "That's not my business — edit my number",

  // Legal-name confirm — ambiguous match. MC and DOT number ranges overlap, so the same
  // digits routinely name two different companies; the lookup returns both and the customer
  // picks which is theirs.
  ambiguousTitle: 'Which one is your business?',
  ambiguousNoneLabel: 'Neither — let me fix my number',

  // NOT FOUND — the registry answered, and it does not know this number. John's round-2 call:
  // this one BLOCKS. A number the registry says is wrong is a number we should not save, so the
  // only ways off this screen are fixing the number and re-running the lookup.
  lookupNotFoundTitle: "We couldn't verify that number",
  lookupNotFoundBodyPrefix: 'Nothing matched ',
  lookupNotFoundBodySuffix:
    ' in the carrier registry. Please check for any typos or try your other authority number.',
  // REGISTRY DOWN — separate from the not-found copy on purpose. THT-738 makes verification
  // REQUIRED, so this state no longer offers a way past it either: the copy asks for a retry in
  // a few minutes, because a registry we could not reach is no evidence about the number and
  // waiting is the thing that actually helps.
  lookupFailedTitle: "We couldn't check that number right now",
  lookupFailedBody:
    "We couldn't reach the carrier registry just now. Please try again in a few minutes.",
  lookupFailedNumberLabel: 'Your MC or DOT number',
  /** Registry-down only — the not-found screen's own body already says what to check. */
  lookupFailedTip:
    'Tip: your MC number is on your operating authority letter. Your DOT number is on the side of your truck.',
  tryAgainLabel: 'Try the lookup again',

  /**
   * Shown on either failure screen once the stuck alert has actually fired (THT-738, the 3rd
   * consecutive failure). Only ever rendered after the call has gone out — promising a human
   * who was never told would be worse than saying nothing.
   */
  teamNotified: "We've let our team know — they may reach out to help.",

  // Save (PATCH /customers) failed. Unlike a lookup failure this one loses the answer, so it
  // is surfaced rather than swallowed — the customer can retry, or move on and re-enter the
  // details on Business Info.
  saveFailed: "We couldn't save that just now. Try again?",
  saveRetryLabel: 'Try again',
  skipForNowLabel: "Skip for now — you'll enter this on the next screen",
} as const;

/** Which identifier resolved a match, e.g. "MC 149513" — the design's candidate eyebrow. */
export const formatCarrierIdentifierLabel = (
  identifierType: string,
  identifier: string
) => `${identifierType} ${identifier}`;

/**
 * Accessible name for a candidate card, e.g. "MC 149513 — ACORD TRANSPORTATION INC". The card
 * shows the identifier and the legal name on separate lines; screen readers get them joined,
 * because which identifier matched is the only thing telling the two rows apart.
 */
export const formatCarrierCandidateLabel = (
  identifierType: string,
  identifier: string,
  legalName: string
) =>
  `${formatCarrierIdentifierLabel(identifierType, identifier)} — ${legalName}`;

/** Sub-head on the single-match confirm screen. */
export const formatConfirmBody = (matchedLabel: string) =>
  `This is what the carrier registry has on file for ${matchedLabel}.`;
